import { Link } from "react-router-dom";

export default function PrivacyPage() {
  return (
    <div style={{ minHeight: "100vh", background: "#F8F9FA", color: "#1A1A1A" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "48px 24px 80px" }}>
        <Link to="/" style={{ color: "#0B9FBD", textDecoration: "none", fontSize: 14, display: "inline-block", marginBottom: 24 }}>
          &larr; Back to LiveBetter
        </Link>
        <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, fontFamily: "Outfit, sans-serif" }}>Privacy Policy</h1>
        <p style={{ color: "#666", marginBottom: 40 }}>Last updated: March 29, 2026</p>

        <Section title="1. Introduction">
          <p>
            LiveBetter ("we", "our", or "us") operates the LiveBetter mobile application and web
            platform (collectively, the "Service"). This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our Service.
          </p>
          <p>
            By using LiveBetter, you agree to the collection and use of information in accordance with
            this policy. If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <h3 style={h3Style}>2.1 Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul style={ulStyle}>
            <li>Name and email address</li>
            <li>Profile picture (if provided)</li>
            <li>Authentication credentials (password is stored as a one-way hash; we never store plain-text passwords)</li>
            <li>Google account information if you sign in with Google</li>
          </ul>

          <h3 style={h3Style}>2.2 Health and Wellness Data</h3>
          <p>To provide personalized insights, we collect data you voluntarily submit:</p>
          <ul style={ulStyle}>
            <li>Daily check-in responses (mood, stress, energy, focus, sleep quality)</li>
            <li>Health metrics (sleep hours, heart rate, steps, activity level, hydration, caffeine and alcohol intake)</li>
            <li>Meal logs (meal type, timing, description)</li>
            <li>Evening reflections and free-text notes</li>
            <li>Time capsule messages</li>
          </ul>

          <h3 style={h3Style}>2.3 Location Data</h3>
          <p>
            If you enable environment-aware insights, we collect your approximate location
            (latitude/longitude) solely to retrieve local weather, air quality, and pollen data. We do
            not track your location continuously or store location history beyond the current session.
          </p>

          <h3 style={h3Style}>2.4 Usage and Analytics Data</h3>
          <ul style={ulStyle}>
            <li>Feature usage (which features you use and how often)</li>
            <li>App interaction events (views, completions, shares)</li>
            <li>Device type and operating system</li>
          </ul>

          <h3 style={h3Style}>2.5 Payment Information</h3>
          <p>
            Payment processing is handled entirely by Stripe. We do not store your credit card number,
            bank account, or other financial details. We only retain your subscription tier and
            transaction status.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use the collected information to:</p>
          <ul style={ulStyle}>
            <li>Provide, maintain, and improve the Service</li>
            <li>Generate personalized wellness scores, insights, and recommendations</li>
            <li>Power AI coaching conversations tailored to your health data</li>
            <li>Produce weekly and monthly wellness reports</li>
            <li>Detect health risk patterns (stress accumulation, sleep disruption, burnout trends)</li>
            <li>Enable social features (circles, partnerships, nudges) when you opt in</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send notifications you have opted into (check-in reminders, weekly reports)</li>
            <li>Improve our AI models and service quality using aggregated, de-identified data</li>
          </ul>
        </Section>

        <Section title="4. AI and Third-Party Services">
          <p>We use the following third-party services to operate LiveBetter:</p>
          <ul style={ulStyle}>
            <li>
              <strong>AI Providers (NVIDIA, OpenAI):</strong> We send aggregated, contextual health
              metrics to AI models to generate insights, predictions, and coaching responses. We do not
              send your name, email, or other personally identifiable information to AI providers.
            </li>
            <li>
              <strong>OpenWeatherMap:</strong> We send your approximate coordinates to retrieve
              weather and air quality data. OpenWeatherMap does not receive your identity.
            </li>
            <li>
              <strong>Stripe:</strong> Handles all payment processing. Subject to{" "}
              <a href="https://stripe.com/privacy" style={linkStyle} target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.
            </li>
            <li>
              <strong>Supabase:</strong> Provides database hosting and file storage infrastructure.
              Your data is stored in PostgreSQL databases with encryption at rest.
            </li>
          </ul>
        </Section>

        <Section title="5. Data Sharing and Disclosure">
          <p>We do not sell your personal data. We may share information only in these cases:</p>
          <ul style={ulStyle}>
            <li>
              <strong>With your consent:</strong> If you enable data sharing in settings, your
              anonymized, aggregated data may be included in comparative benchmarks visible to other
              users.
            </li>
            <li>
              <strong>Social features:</strong> If you join circles or partnerships, limited wellness
              data (scores, streaks) is shared with members of those groups.
            </li>
            <li>
              <strong>Organizational accounts:</strong> If your employer provides LiveBetter, aggregated
              team-level statistics (never individual data) may be visible to organization
              administrators.
            </li>
            <li>
              <strong>Legal requirements:</strong> We may disclose data if required by law, legal
              process, or government request.
            </li>
            <li>
              <strong>Service providers:</strong> Third-party services listed in Section 4 receive only
              the minimum data necessary to provide their function.
            </li>
          </ul>
        </Section>

        <Section title="6. Data Retention">
          <ul style={ulStyle}>
            <li>Your health data is retained as long as your account is active.</li>
            <li>Session tokens expire after 7 days of inactivity.</li>
            <li>
              When you delete your account, all associated data is permanently removed from our
              systems, including check-ins, health data, insights, coach conversations, social
              connections, analytics events, and payment records.
            </li>
          </ul>
        </Section>

        <Section title="7. Data Export and Portability">
          <p>
            You can export your personal data at any time in CSV, JSON, or PDF format through the app
            settings. Exports include your check-ins, health metrics, scores, and insights.
          </p>
        </Section>

        <Section title="8. Data Security">
          <p>We implement industry-standard security measures to protect your data:</p>
          <ul style={ulStyle}>
            <li>Passwords are hashed using bcrypt</li>
            <li>All data transmission uses HTTPS/TLS encryption</li>
            <li>Authentication cookies are HttpOnly and Secure</li>
            <li>Database access is restricted and uses connection pooling</li>
            <li>Data at rest is encrypted on our database provider (Supabase)</li>
          </ul>
          <p>
            While we strive to protect your data, no method of electronic transmission or storage is
            100% secure. We cannot guarantee absolute security.
          </p>
        </Section>

        <Section title="9. Your Rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul style={ulStyle}>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Export your data in a portable format</li>
            <li>Withdraw consent for optional data processing (e.g., data sharing, location)</li>
            <li>Opt out of non-essential notifications</li>
          </ul>
          <p>
            To exercise any of these rights, visit your account settings or contact us at{" "}
            <a href="mailto:privacy@livebetter.one" style={linkStyle}>privacy@livebetter.one</a>.
          </p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            LiveBetter is not intended for users under the age of 13. We do not knowingly collect
            personal information from children under 13. If we learn that we have collected data from a
            child under 13, we will delete it promptly.
          </p>
        </Section>

        <Section title="11. Health Disclaimer">
          <p>
            LiveBetter provides general wellness information and is not a medical device or service.
            Our insights, scores, and recommendations are for informational purposes only and do not
            constitute medical advice, diagnosis, or treatment. Always consult a qualified healthcare
            professional for medical concerns.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify you of significant
            changes via email or in-app notification. Your continued use of the Service after changes
            become effective constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>If you have questions about this Privacy Policy, contact us at:</p>
          <ul style={{ ...ulStyle, listStyle: "none", paddingLeft: 0 }}>
            <li>Email: <a href="mailto:privacy@livebetter.one" style={linkStyle}>privacy@livebetter.one</a></li>
            <li>Support: <a href="mailto:support@livebetter.one" style={linkStyle}>support@livebetter.one</a></li>
          </ul>
        </Section>

        <Footer />
      </div>
    </div>
  );
}

const h3Style = { fontSize: 16, fontWeight: 600, marginTop: 20, marginBottom: 8 };
const ulStyle = { paddingLeft: 24, lineHeight: 1.8 };
const linkStyle = { color: "#0B9FBD", textDecoration: "underline" };

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12, fontFamily: "Outfit, sans-serif" }}>{title}</h2>
      <div style={{ lineHeight: 1.7, color: "#333" }}>{children}</div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{ marginTop: 64, paddingTop: 24, borderTop: "1px solid #E2E8F0", display: "flex", gap: 24, fontSize: 14, color: "#64748B" }}>
      <Link to="/privacy" style={{ color: "#64748B", textDecoration: "none" }}>Privacy Policy</Link>
      <Link to="/terms" style={{ color: "#64748B", textDecoration: "none" }}>Terms of Service</Link>
      <span style={{ marginLeft: "auto" }}>LiveBetter {new Date().getFullYear()}</span>
    </footer>
  );
}
